import React from "react";
import { useTranslation } from "react-i18next";
import {
    Filter,
    Grid,
    useBaseAppContext,
    useResourceApi,
    numberFormat,
    useFilterContext,
} from "@programari-limit/base-react";
import MuiGrid from "@mui/material/Grid";
import { columnesArticle } from "sharedAdvancedSearch/advancedSearchColumns";
import {
    Box,
    Divider,
    Icon,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import { SpringFilterQueryBuilder } from "utils/spring-filter-query-builder";
import GridFilterField from "components/GridFilterField";

const FilterContent = (props) => {
    const { filterApiRef, setNamedQueries } = props;
    const { t } = useTranslation();
    const { data } = useFilterContext();

    const buildNamedQueries = React.useMemo(
        () => [
            `tipusOperacio#${data?.tipusOperacio}`,
            `dataInici#${data?.dataInici}`,
            `dataFi#${data?.dataFi}`,
            `articleId#${data?.article?.id}`,
            `embarcacioId#${data?.embarcacio?.id}`,
        ],
        [data]
    );

    React.useEffect(() => {
        setNamedQueries(buildNamedQueries);
    }, [buildNamedQueries]);

    return (
        <MuiGrid container spacing={2}>
            <GridFilterField name="tipusOperacio" md={6} lg={2} />
            <GridFilterField name="dataInici" xs={6} md={6} lg={1.75} />
            <GridFilterField name="dataFi" xs={6} md={6} lg={1.75} />
            <GridFilterField
                name="article"
                md={6}
                lg={3}
                advancedSearchColumns={columnesArticle}
                advancedSearchMaxWidth={"lg"}
            />
            <GridFilterField name="embarcacio" md={6} lg={3} />

            <MuiGrid item lg={0.5}>
                <Tooltip title={t("comu.resetFiltre")}>
                    <IconButton
                        onClick={() => filterApiRef?.current?.doReset()}
                    >
                        <Icon>filter_alt_off</Icon>
                    </IconButton>
                </Tooltip>
            </MuiGrid>
        </MuiGrid>
    );
};

const TotalsContent = (props) => {
    const { totals } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useBaseAppContext();

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 5,
                mt: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.linies.totals.totalQuantitat")}
                </Typography>
                <Typography>
                    {numberFormat(totals.totalQuantitat, null, currentLanguage)}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.totals.operacions")}
                </Typography>
                <Typography>
                    {numberFormat(
                        totals.totalOperacions,
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
        </Box>
    );
};

const springFilterBuilder = (data) => {
    const { and, equal, ge, le } = SpringFilterQueryBuilder;

    const filter = and(
        equal("operacioLinia.operacio.tipusOperacio", data?.tipusOperacio),
        ge(
            "operacioLinia.operacio.data",
            data?.dataInici ? data?.dataInici + "T00:00:00" : undefined
        ),
        le(
            "operacioLinia.operacio.data",
            data?.dataFi ? data?.dataFi + "T23:59:59" : undefined
        ),
        equal("operacioLinia.operacio.embarcacio.id", data?.embarcacio?.id),
        equal("article.id", data?.article?.id)
    ).toString();

    return filter;
};

const OperacioLiniaArticleFilter = (props) => {
    const { onSpringFilterChange, onFilterInitialized, setNamedQueries } =
        props;
    const filterApiRef = React.useRef();

    return (
        <Box sx={{ mb: 2 }}>
            <Filter
                name={"OLA_FILTER"}
                resourceName={"operacioLiniaArticle"}
                persistentState
                springFilterBuilder={springFilterBuilder}
                onSpringFilterChange={onSpringFilterChange}
                onFilterInitialized={onFilterInitialized}
                size="small"
                apiRef={filterApiRef}
                initialOnChangeRequest
            >
                <FilterContent
                    filterApiRef={filterApiRef}
                    setNamedQueries={setNamedQueries}
                />
            </Filter>
        </Box>
    );
};

const OperacioLiniaArticle = ({ noOperacio, operacio }) => {
    const { t } = useTranslation();
    const [fixedFilter, setFixedFilter] = React.useState();
    const [namedQueries, setNamedQueries] = React.useState([]);
    const [filterInitialized, setFilterInitialized] = React.useState(false);
    const gridApiRef = React.useRef();
    const { currentLanguage } = useBaseAppContext();
    const { apiInitialized, exec: apiExec } = useResourceApi(
        "operacioLiniaArticle"
    );
    const [totals, setTotals] = React.useState();

    const columns = [
        {
            name: "operacioLinia",
            flex: 2,
        },
        {
            name: "article",
            flex: 1.5,
        },
        {
            name: "quantitat",
            flex: 0.5,
        },
        {
            name: "quantitatAnterior",
            flex: 0.75,
        },
        {
            name: "quantitatPosterior",
            flex: 0.75,
        },
    ];

    if (noOperacio) {
        columns.unshift(
            {
                name: "numeroOperacio",
                flex: 0.5,
                sortable: false,
            },
            { name: "dataOperacio", flex: 0.75, sortable: false },
            {
                name: "nomEmbarcacio",
                flex: 0.75,
                sortable: false,
            }
        );
    }

    // Todo: S'ha posat aixo pq la llibreria no refresca la traduccio de l'accio sense refrescar el grid
    React.useEffect(() => {
        gridApiRef?.current?.doRefresh();
    }, [currentLanguage]);

    const obtenirTotals = () => {
        apiExec("exec_OLA_OBTENIR_TOTALS", {
            data: { springFilter: fixedFilter ?? undefined }, // TODO: Ha de poder rebre undefined com a filtre o text buid
            callbacks: {
                setResponse: (response) => {
                    const resource = {
                        ...response.data,
                        _resource: response,
                        _links: response.links,
                    };
                    setTotals(resource);
                },
                setError: (error) => {},
            },
        });
    };

    React.useEffect(() => {
        if (apiInitialized && filterInitialized) {
            obtenirTotals();
        }
    }, [apiInitialized, filterInitialized, fixedFilter]);

    return (
        <Grid
            title={t("operacio.linies.liniesArticle.title")}
            resourceName="operacioLiniaArticle"
            columns={columns}
            readOnly
            namedQueries={namedQueries}
            findDisabled={noOperacio && !filterInitialized}
            actionExecEnabled
            onRefresh={() => obtenirTotals()}
            onDelete={() => obtenirTotals()}
            fixedFilter={
                noOperacio
                    ? fixedFilter
                    : `operacioLinia.operacio.id : ${operacio.id}`
            }
            toolbarAdditionalRow={
                noOperacio ? (
                    <>
                        <OperacioLiniaArticleFilter
                            onSpringFilterChange={setFixedFilter}
                            onFilterInitialized={() =>
                                setFilterInitialized(true)
                            }
                            setNamedQueries={setNamedQueries}
                        />
                        {totals && (
                            <Box sx={{ mb: 2 }}>
                                <TotalsContent totals={totals} />
                            </Box>
                        )}
                    </>
                ) : undefined
            }
            density="compact"
            // formPopupEditable
            // formPopupComponent={<OperacioLiniaArticleForm />}
            // formPopupTitleCreate={t("operacio.linies.liniesArticle.create")}
            // formPopupTitleUpdate={t("operacio.linies.liniesArticle.update")}
            // formPopupSize="sm"
        />
    );
};
export default OperacioLiniaArticle;
